import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import './LatestInsightsSection.css'
import { decorateInsight } from './latestInsightsData'

function SkeletonInsightCard() {
  return (
    <article className="li-card li-in li-on animate-pulse" aria-hidden="true">
      <div className="li-img-wrap" style={{ background: 'rgba(180,83,9,.10)' }} />
      <div className="li-body space-y-3">
        <div className="h-2.5 w-1/3 rounded" style={{ background: 'rgba(180,83,9,.10)' }} />
        <div className="h-4 w-5/6 rounded" style={{ background: 'rgba(180,83,9,.14)' }} />
        <div className="h-2.5 w-full rounded" style={{ background: 'rgba(180,83,9,.08)' }} />
        <div className="h-2.5 w-4/5 rounded" style={{ background: 'rgba(180,83,9,.08)' }} />
      </div>
    </article>
  )
}

function InsightCard({ item, entered }) {
  return (
    <article className={`li-card li-in${entered ? ' li-on' : ''}`} style={{ animationDelay: item.delay }}>
      <div className="li-img-wrap">
        <img className="li-img" src={item.img} alt={item.alt} loading="lazy" />
        <div className="li-img-overlay" />
        <span className="li-badge">{item.badge}</span>
      </div>
      <div className="li-body">
        <div className="li-meta">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 2v4M16 2v4" />
            <rect width="18" height="18" x="3" y="4" rx="2" />
            <path d="M3 10h18" />
          </svg>
          {item.date}
          <span className="li-meta-dot" />
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          {item.readTime}
        </div>
        <h3 className="li-title">{item.title}</h3>
        <p className="li-excerpt">{item.excerpt}</p>
        <div className="li-footer">
          <Link to={`/blog/${item.slug ?? item.id}`} className="li-read">
            Read More
            <svg className="li-read-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7H12M12 7L8 3M12 7L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
          <svg className="li-book-ico" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 7v14" />
            <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
          </svg>
        </div>
      </div>
    </article>
  )
}

export default function LatestInsightsSection() {
  const sectionRef = useRef(null)
  const [entered, setEntered] = useState(false)

  // Latest three published posts; the endpoint returns a raw Laravel paginator.
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: () => api.blog.posts(),
  })

  const insights = useMemo(
    () => (data?.data ?? []).slice(0, 3).map(decorateInsight),
    [data],
  )

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setEntered(true)
        observer.disconnect()
      },
      { threshold: 0.08 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <section id="latest-insights" ref={sectionRef}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.03 }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="li-dots-pattern" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="#B45309" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#li-dots-pattern)" />
        </svg>
        <div style={{ position: 'absolute', top: '-70px', right: '-70px', width: '380px', height: '380px', opacity: 0.055 }}>
          <svg viewBox="0 0 380 380" xmlns="http://www.w3.org/2000/svg">
            <circle cx="190" cy="190" r="170" fill="none" stroke="#B45309" strokeWidth="1" />
            <circle cx="190" cy="190" r="128" fill="none" stroke="#D97706" strokeWidth="0.7" />
            <circle cx="190" cy="190" r="86" fill="none" stroke="#F59E0B" strokeWidth="0.5" strokeDasharray="4 6" />
          </svg>
        </div>
        <div style={{ position: 'absolute', bottom: '-50px', left: '-50px', width: '260px', height: '260px', opacity: 0.04 }}>
          <svg viewBox="0 0 260 260" xmlns="http://www.w3.org/2000/svg">
            <circle cx="130" cy="130" r="115" fill="none" stroke="#B45309" strokeWidth="1" />
            <circle cx="130" cy="130" r="76" fill="none" stroke="#D97706" strokeWidth="0.6" />
          </svg>
        </div>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 60% 45% at 50% 0%,rgba(217,119,6,0.07) 0%,transparent 65%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(180,83,9,0.30),transparent)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(180,83,9,0.30),transparent)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div id="li-header" className="flex items-end justify-between flex-wrap gap-5" style={{ marginBottom: '52px' }}>
          <div className={`li-in${entered ? ' li-on' : ''}`} style={{ animationDelay: '.04s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span style={{ height: '1px', width: '28px', background: 'rgba(180,83,9,0.5)', display: 'block', flexShrink: 0 }} />
              <span style={{ fontFamily: 'var(--font-heading),serif', fontSize: '9.5px', fontWeight: 600, letterSpacing: '.32em', color: '#B45309', textTransform: 'uppercase' }}>
                Spiritual Knowledge
              </span>
              <span style={{ height: '1px', width: '28px', background: 'rgba(180,83,9,0.5)', display: 'block', flexShrink: 0 }} />
            </div>
            <h2 className="li-heading">
              Latest Insights from <span>The Maharaj</span>
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '14px 0 12px' }}>
              <span style={{ height: '1px', width: '44px', background: 'linear-gradient(90deg,rgba(217,119,6,0.4),transparent)', display: 'block' }} />
              <span style={{ fontSize: '18px', color: '#D97706', opacity: 0.6, lineHeight: 1 }}>ॐ</span>
              <span style={{ height: '1px', width: '44px', background: 'linear-gradient(90deg,transparent,rgba(217,119,6,0.4))', display: 'block' }} />
            </div>
            <p className="li-subtext">High-precision spiritual analysis and Vedic wisdom to help you synchronize with the universe.</p>
          </div>
          <Link to="/blog" className={`li-header-cta li-in-r${entered ? ' li-on' : ''}`} style={{ animationDelay: '.10s' }}>
            Explore All Insights
            <svg className="li-cta-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7H12M12 7L8 3M12 7L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>

        {isError && (
          <div className="py-14 text-center">
            <div className="text-5xl mb-4 opacity-40">🛰️</div>
            <p style={{ fontFamily: 'var(--font-heading),serif', fontSize: '17px', fontWeight: 700, color: '#3B2A1A', marginBottom: '4px' }}>
              Couldn’t Load Insights
            </p>
            <p className="text-sm mb-5" style={{ color: '#8B7355' }}>
              Something went wrong while fetching live data. Please try again.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(180,83,9,.35)', color: '#B45309', background: 'rgba(180,83,9,.06)' }}
            >
              Retry
            </button>
          </div>
        )}

        <div id="li-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" style={{ gap: '24px' }}>
          {isPending
            ? Array.from({ length: 3 }, (_, i) => <SkeletonInsightCard key={i} />)
            : insights.map((item) => <InsightCard item={item} entered={entered} key={item.id} />)}
        </div>
      </div>
    </section>
  )
}
